import { Component, EventEmitter, Input, OnInit, Output } from '@angular/core';
import { VehiclesService } from 'src/app/core/services/vehicles.service';
import { Vehicles } from '../../model/vehicle.model';
import {
  FormBuilder,
  FormGroup,
  FormsModule,
  ReactiveFormsModule
} from '@angular/forms';
import { CommonModule } from '@angular/common';
import { I18NextModule } from 'angular-i18next';
import { MatInputModule } from '@angular/material/input';
import { MatRadioModule } from '@angular/material/radio';
import { MatSelectModule } from '@angular/material/select';
import { CarpoolCenterDateData } from '../../model/order.model';
import i18next from 'i18next';
import { CarCardComponent } from '../car-card/car-card.component';
import { RequestType } from '../../model/requestType.enum';
import { Campaign } from '../../model/campaign.model';
import { HttpErrorResponse } from '@angular/common/http';
import { User } from '../../model/user.model';
import { ActivatedRoute, Router } from '@angular/router';
import { MatDialog } from '@angular/material/dialog';
import { FeedbackModalComponent } from '../feedback-modal/feedback-modal.component';
import { ModalContinueComponent } from '../modal-continue/modal-continue.component';
import { CampaignTypes } from '../../model/campaign.enum';
import { BreakpointObserver } from '@angular/cdk/layout';
import { MatIconModule } from '@angular/material/icon';
import { MatButtonToggleModule } from '@angular/material/button-toggle';
import { SharedModule } from '../../shared.module';
import { MatSliderModule } from '@angular/material/slider';
import { debounceTime } from 'rxjs/operators';
import { CarDetailModalComponent } from '../car-detail-modal/car-detail-modal.component';
import { OrderService } from 'src/app/core/services/order.service';

@Component({
  standalone: true,
  selector: 'app-stock-vehicles',
  templateUrl: './stock-vehicles.component.html',
  styleUrls: ['./stock-vehicles.component.scss'],
  imports: [
    CommonModule,
    I18NextModule,
    FormsModule,
    ReactiveFormsModule,
    MatInputModule,
    MatRadioModule,
    MatSelectModule,
    CarCardComponent,
    MatIconModule,
    MatButtonToggleModule,
    SharedModule,
    MatSliderModule
  ]
})
export class StockVehiclesComponent implements OnInit {
  vehicles: any;
  vehiclesFullList: any[] = [];
  displayedVehicles: any[] = [];
  form: FormGroup;
  selectedVehicle: any;
  spinner = true;
  noStock = false;
  noData = i18next.t('noData');
  requestTypeEnum = RequestType;
  campaigns: Campaign[] = [];
  campaignType = '';
  campaignTypeEnum = CampaignTypes;
  pageSize = 50;
  currentPage = 1;
  sortColumn: string = '';
  sortDirection: 'asc' | 'desc' = 'asc';

  viewMode: 'cards' | 'table' = 'cards';
  isMobile = false;

  models: string[] = [];
  finishes: string[] = [];
  fuels: string[] = [];
  minPrice: number = 0;
  maxPrice: number = 0;
  selectedPrice: number = 0;
  bookedReservations: Array<{ id: string; VIN: string }> = [];
  bookedVins = new Set<string>();
  bookedVehicles: any[] = [];
  visibleVoVehicles: any[] = [];

  @Output() carList = new EventEmitter<any[]>();
  @Output() selectedVehicleChange = new EventEmitter<any>();
  @Output() onBack = new EventEmitter<void>();
  @Output() campaignEmit = new EventEmitter<any>();

  @Input() requestType!: string;
  @Input() user!: User;
  @Input() carPoolCenterDate?: CarpoolCenterDateData;

  constructor(
    private vehiclesService: VehiclesService,
    private orderService: OrderService,
    private fb: FormBuilder,
    private route: ActivatedRoute,
    public dialog: MatDialog,
    private breakpointObserver: BreakpointObserver,
    private router: Router
  ) {
    this.form = this.fb.group({
      selectedVehicle: [''],
      // Filtros para VO
      filterModel: [''],
      filterFinish: [''],
      filterFuel: [''],
      priceMin: [0],
      priceMax: [0]
    });

    this.breakpointObserver
      .observe(['(max-width: 1024px)'])
      .subscribe((result) => {
        this.isMobile = result.matches;
        if (this.isMobile) this.viewMode = 'cards';
      });
  }

  ngOnInit(): void {
    this.loadData();

    this.form
      .get('priceMin')
      ?.valueChanges.pipe(debounceTime(300))
      .subscribe(() => this.searchVehicles());
    this.form
      .get('priceMax')
      ?.valueChanges.pipe(debounceTime(300))
      .subscribe(() => this.searchVehicles());

    this.form.get('selectedVehicle')?.valueChanges.subscribe((value) => {
      this.selectedVehicle = this.vehicles?.find(
        (vehicle: any) => vehicle.VIN === value
      );
    });

    if (this.requestType === this.requestTypeEnum.vo) {
      this.getBookingsVO();
    }

    this.form.get('filterModel')?.valueChanges.subscribe(() => {
      this.updateDynamicVoFilterOptions();
    });

    this.form.get('filterFinish')?.valueChanges.subscribe(() => {
      this.updateDynamicVoFilterOptions();
    });

    this.form.get('filterFuel')?.valueChanges.subscribe(() => {
      this.updateDynamicVoFilterOptions();
    });
  }

  sort(column: string): void {
    // Si clicamos en la misma columna, invertimos el orden. Si es nueva, empezamos por ASC.
    if (this.sortColumn === column) {
      this.sortDirection = this.sortDirection === 'asc' ? 'desc' : 'asc';
    } else {
      this.sortColumn = column;
      this.sortDirection = 'asc';
    }

    this.visibleVoVehicles.sort((a: any, b: any) => {
      const valA = this.getSortValue(a, column);
      const valB = this.getSortValue(b, column);

      // Lógica para valores nulos
      if (valA === null || valA === undefined) return 1;
      if (valB === null || valB === undefined) return -1;

      // Comparación
      if (valA < valB) return this.sortDirection === 'asc' ? -1 : 1;
      if (valA > valB) return this.sortDirection === 'asc' ? 1 : -1;
      return 0;
    });

    // Importante: Actualizar la lista visual después de ordenar
    this.currentPage = 1;
    this.updateDisplayedVehicles();
  }

  // Función auxiliar para obtener el valor según la ruta del objeto
  private getSortValue(vehicle: any, column: string): any {
    switch (column) {
      case 'model':
        return vehicle.model?.toLowerCase();
      case 'plate':
        return vehicle.plateNumber?.toLowerCase();
      case 'plateRegistrationDate':
        return vehicle.plateRegistrationDate?.toLowerCase();
      case 'kms':
        return vehicle.vehicleGroups[0]?.kms || 0;
      case 'color':
        return vehicle.exteriorColor?.toLowerCase();
      case 'VNPrice':
        return vehicle.vehicleGroups[0]?.prices?.priceVN || 0;
      case 'VOPrice':
        return vehicle.vehicleGroups[0]?.prices?.priceVO || 0;
      case 'bookings':
        return vehicle.bookingsVo;
      default:
        return '';
    }
  }

  loadData(): void {
    this.spinner = true;
    if (this.requestType === this.requestTypeEnum.campaigns) {
      this.getCampaignsVehicles();
    } else {
      this.getVehicles();
    }
  }

  getBookingsVO(): void {
    this.spinner = true;

    this.vehiclesService
      .getVehiclesByVehicleTypeAndNIS(this.requestType)
      .subscribe({
        next: (resp: Array<{ id: string; VIN: string }>) => {
          this.bookedReservations = resp;
          this.bookedVins = new Set(resp.map((x) => x.VIN));
          this.updateBookedVehicles(); // intenta cruzar si ya hay stock
        },
        error: () => {
          this.spinner = false;
          this.noStock = true;
        }
      });
  }

  private updateBookedVehicles(): void {
    const list = this.vehiclesFullList?.length
      ? this.vehiclesFullList
      : this.vehicles;

    if (!list?.length || !this.bookedReservations?.length) {
      this.bookedVehicles = [];
      return;
    }

    this.bookedVehicles = list
      .filter((vehicle: any) => this.bookedVins.has(vehicle.VIN))
      .map((vehicle: any) => {
        const reservation = this.bookedReservations.find(
          (item) => item.VIN === vehicle.VIN
        );

        return {
          ...vehicle,
          requestNumber: reservation?.id
        };
      });
  }

  getVehicles(): void {
    this.spinner = true;

    this.vehiclesService
      .getStockVehicles(
        this.requestType,
        this.carPoolCenterDate?.center,
        this.carPoolCenterDate?.deliveryDateTime,
        this.carPoolCenterDate?.returnDateTime
      )
      .subscribe({
        next: (resp: Vehicles[] | []) => {
          this.spinner = false;
          this.noStock = resp.length === 0;
          this.vehicles = resp;
          this.vehiclesFullList = resp;
          if (this.requestType === this.requestTypeEnum.vo) {
            this.visibleVoVehicles = this.getUnblockedVoVehicles(resp);
            this.noStock = this.visibleVoVehicles.length === 0;
            if (this.models.length === 0) {
              this.extractFilterOptions(this.visibleVoVehicles);
            }
            this.sort('model');
            this.currentPage = 1;
            this.updateDisplayedVehicles();
            this.updateBookedVehicles();
          }
        },
        error: () => {
          this.spinner = false;
          this.noStock = true;
        }
      });
  }

  private getUnblockedVoVehicles(list: any[] = []): any[] {
    return list.filter(
      (vehicle: any) => vehicle.vehicleGroups?.[0]?.blocked === false
    );
  }

  searchVehicles(): void {
    this.updateDynamicVoFilterOptions();

    const values = this.form.getRawValue();
    const fModel = values.filterModel;
    const fFinish = values.filterFinish;
    const fFuel = values.filterFuel;
    const fPriceMin = values.priceMin;
    const fPriceMax = values.priceMax;

    const baseList = this.getUnblockedVoVehicles(this.vehiclesFullList);

    const filtered = baseList.filter((car) => {
      const carPrice = car.vehicleGroups[0]?.prices?.priceVO;

      const matchesModel = !fModel || car.modelName === fModel;
      const matchesFinish = !fFinish || car.finish === fFinish;
      const matchesFuel = !fFuel || car.fuelType === fFuel;
      const matchesPrice = carPrice >= fPriceMin && carPrice <= fPriceMax;

      return matchesModel && matchesFinish && matchesFuel && matchesPrice;
    });

    this.visibleVoVehicles = filtered;
    this.noStock = this.visibleVoVehicles.length === 0;

    const columnToSort = this.sortColumn ? this.sortColumn : 'model';
    const currentDir = this.sortDirection;
    this.sortColumn = '';
    this.sortDirection = currentDir === 'desc' ? 'asc' : 'desc';

    this.sort(columnToSort);

    this.currentPage = 1;
    this.updateDisplayedVehicles();
  }

  updateDisplayedVehicles(): void {
    const limit = this.currentPage * this.pageSize;
    this.displayedVehicles = this.visibleVoVehicles.slice(0, limit);
  }

  // Función que se dispara al hacer scroll
  onScroll(event: any): void {
    if (this.requestType !== this.requestTypeEnum.vo) return;
    const element = event.target;
    if (
      element.scrollHeight - element.scrollTop <=
      element.clientHeight + 100
    ) {
      if (this.displayedVehicles.length < this.visibleVoVehicles.length) {
        this.currentPage++;
        this.updateDisplayedVehicles();
      }
    }
  }

  extractFilterOptions(data: any[]): void {
    this.models = [...new Set(data.map((v) => v.modelName))]
      .filter(Boolean)
      .sort();
    this.finishes = [...new Set(data.map((v) => v.finish))]
      .filter(Boolean)
      .sort();
    this.fuels = [...new Set(data.map((v) => v.fuelType))]
      .filter(Boolean)
      .sort();

    const allPrices = data
      .map((v) => v.vehicleGroups[0]?.prices?.priceVO)
      .filter((p) => p != null);

    if (allPrices.length > 0) {
      this.minPrice = Math.min(...allPrices);
      this.maxPrice = Math.max(...allPrices);

      this.form.patchValue({
        priceMin: this.minPrice,
        priceMax: this.maxPrice
      });
    }

    setTimeout(() => {
      this.searchVehicles();
    }, 0);
  }

  getCampaignsVehicles() {
    this.route.params.subscribe((params) => {
      const campaignId = params['id'];
      this.vehiclesService.getCampaignsVehicles(this.user.category).subscribe({
        next: (res) => {
          this.campaigns = res;
          this.campaigns.forEach((campaign) => {
            if (campaign.id === campaignId) {
              this.vehicles = campaign.vehicleGroups.filter(
                (vehicleGroup) => vehicleGroup.remainingStock > 0
              );
              this.campaignType = campaign.type;
              this.campaignEmit.emit(campaign);
            }
          });
          this.spinner = false;
        },
        error: (err: HttpErrorResponse) => {
          this.spinner = false;
          console.error(err);
        }
      });
    });
  }

  vehicleReplacement(vehicle: any) {
    if (
      this.requestType === this.requestTypeEnum.leasing &&
      !vehicle.VIN &&
      !vehicle.externalId
    ) {
      this.dialog.open(FeedbackModalComponent, {
        data: {
          text: i18next.t('notVinError'),
          close: true,
          theme: 'ligth'
        }
      });
    } else if (this.requestType === this.requestTypeEnum.vo) {
      if (this.bookedVehicles.length >= 2) {
        this.dialog.open(FeedbackModalComponent, {
          data: {
            text: i18next.t('maxBookedVehiclesError'),
            close: true,
            theme: 'ligth'
          }
        });
      } else if (vehicle.VIN && this.bookedVins?.has(vehicle.VIN)) {
        this.dialog.open(FeedbackModalComponent, {
          data: {
            text: i18next.t('vehicleAlreadyBookedError'),
            close: true,
            theme: 'ligth'
          }
        });
      } else if (
        vehicle.vehicleGroups?.[0]?.bookingsVo &&
        vehicle.vehicleGroups[0].bookingsVo >= 2
      ) {
        this.dialog.open(FeedbackModalComponent, {
          data: {
            text: i18next.t('vehicleMaxBookingsError'),
            close: true,
            theme: 'ligth'
          }
        });
      } else {
        this.selectedVehicle = vehicle;
        this.form
          .get('selectedVehicle')
          ?.setValue(vehicle.VIN, { emitEvent: false });
        this.selectedVehicleChange.emit(this.selectedVehicle);
      }
    } else {
      this.selectedVehicle = vehicle;
      this.form
        .get('selectedVehicle')
        ?.setValue(
          this.requestType === this.requestTypeEnum.leasing &&
            !vehicle.VIN &&
            vehicle.externalId
            ? vehicle.externalId
            : vehicle.VIN,
          { emitEvent: false }
        );
      this.selectedVehicleChange.emit(this.selectedVehicle);
    }
  }

  getFormattedDescriptions(): string {
    if (this.selectedVehicle?.optionals?.length > 0) {
      return this.selectedVehicle.optionals
        .map((optional: any) => optional.description)
        .join(' / ');
    }
    return '';
  }

  handleCarListChange(updatedList: any[]) {
    if (this.campaignType === this.campaignTypeEnum.RAFFLE) {
      const selectedCount = updatedList?.length || 0;
      if (selectedCount < 3) {
        this.openContinueModal(updatedList);
      } else {
        this.carList.emit(updatedList);
      }
    } else {
      this.carList.emit(updatedList);
    }
  }

  openContinueModal(updatedList: any[]) {
    const dialogRef = this.dialog.open(ModalContinueComponent, {
      width: '400px',
      disableClose: true
    });
    dialogRef.afterClosed().subscribe((result) => {
      if (result) {
        this.carList.emit(updatedList);
      }
    });
  }

  changeViewMode(mode: 'cards' | 'table'): void {
    if (!this.isMobile) {
      this.viewMode = mode;
    }
  }

  back(): void {
    this.onBack.emit();
  }

  downloadExcel(): void {
    this.spinner = true;
    this.vehiclesService.getVoCSV(this.requestType).subscribe({
      next: (blob: Blob) => {
        const filename = `stock_${new Date().toISOString().slice(0, 10)}.xlsx`;
        this.saveBlob(blob, filename);
        this.spinner = false;
      },
      error: () => {
        this.spinner = false;
      }
    });
  }

  private saveBlob(blob: Blob, filename: string): void {
    const url = window.URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = filename;
    a.click();
    window.URL.revokeObjectURL(url);
  }

  openCarDetail(car: any, view: boolean): void {
    this.dialog.open(CarDetailModalComponent, {
      width: '1490px',
      panelClass: 'seat-modal',
      data: {
        car,
        requestType: this.requestType,
        theme: 'light',
        view: view
      }
    });
  }

  openCancelModalBook(requestNumber: string): void {
    const dialogRef = this.dialog.open(FeedbackModalComponent, {
      data: {
        cancelOrder: true,
        text: i18next.t('cancelBookVehicle'),
        theme: 'light'
      }
    });

    dialogRef.componentInstance.confirmCancelOrderEmitter.subscribe(() => {
      if (requestNumber) {
        this.spinner = true;
        this.cancelBookVehicle(requestNumber);
      }
    });
  }

  cancelBookVehicle(requestNumber: string): void {
    this.orderService
      .cancelBookVehicle(requestNumber, this.requestType)
      .subscribe({
        next: () => {
          this.spinner = false;
          const dialogRef = this.dialog.open(FeedbackModalComponent, {
            data: {
              text: i18next.t('cancelBookOK'),
              close: true,
              theme: 'light'
            }
          });

          dialogRef.afterClosed().subscribe(() => {
            this.router
              .navigateByUrl('/', { skipLocationChange: true })
              .then(() => {
                this.router.navigate(['/vo']);
              });
          });
        },
        error: () => {
          this.spinner = false;
          this.dialog.open(FeedbackModalComponent, {
            data: {
              text: i18next.t('cancelBookKO'),
              close: true,
              theme: 'light'
            }
          });
        }
      });
  }

  private filterVoVehicles(
    list: any[],
    filters: {
      model?: string;
      finish?: string;
      fuel?: string;
      priceMin?: number;
      priceMax?: number;
    }
  ): any[] {
    return list.filter((car) => {
      const carPrice = car.vehicleGroups?.[0]?.prices?.priceVO ?? 0;

      const matchesModel = !filters.model || car.modelName === filters.model;
      const matchesFinish = !filters.finish || car.finish === filters.finish;
      const matchesFuel = !filters.fuel || car.fuelType === filters.fuel;
      const matchesPrice =
        carPrice >= (filters.priceMin ?? 0) &&
        carPrice <= (filters.priceMax ?? Number.MAX_SAFE_INTEGER);

      return matchesModel && matchesFinish && matchesFuel && matchesPrice;
    });
  }

  private updateDynamicVoFilterOptions(): void {
    const values = this.form.getRawValue();
    const baseList = this.getUnblockedVoVehicles(this.vehiclesFullList);

    const listForModels = this.filterVoVehicles(baseList, {
      finish: values.filterFinish,
      fuel: values.filterFuel,
      priceMin: values.priceMin,
      priceMax: values.priceMax
    });

    const listForFinishes = this.filterVoVehicles(baseList, {
      model: values.filterModel,
      fuel: values.filterFuel,
      priceMin: values.priceMin,
      priceMax: values.priceMax
    });

    const listForFuels = this.filterVoVehicles(baseList, {
      model: values.filterModel,
      finish: values.filterFinish,
      priceMin: values.priceMin,
      priceMax: values.priceMax
    });

    this.models = [...new Set(listForModels.map((v) => v.modelName))]
      .filter(Boolean)
      .sort();

    this.finishes = [...new Set(listForFinishes.map((v) => v.finish))]
      .filter(Boolean)
      .sort();

    this.fuels = [...new Set(listForFuels.map((v) => v.fuelType))]
      .filter(Boolean)
      .sort();
  }
}
